import { encode as urlSafeEncode } from "url-safe-base64";

import { DecisionsOutcomeFilter } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import {
  datasetJobsApi,
  datasetsApi,
  decisionHistoryApiRoute,
  historyTasksApi,
  metricsApi,
  reviewApi,
} from "src/api/exporterApi";
import {
  flowRouterApiGet,
  flowRouterApiPost,
  flowRouterApiPut,
} from "src/api/flowRouterApi";
import {
  CreateDatasetFromHistory,
  CreateDatasetFromScratch,
  GenericObject,
} from "src/api/flowTypes";
import { flowApiUrl } from "src/api/hosts";
import {
  Dataset,
  DatasetColumn,
  DatasetColumnGroup,
  DatasetFileFormat,
  DatasetFileUpload,
  DatasetPurpose,
  DatasetResponse,
  DatasetRow,
  DecideDecisionSuccess,
  DecisionEnvironment,
  DecisionSummaryResponse,
  FlowRunnerResultV2,
  ListReviewCasesResponse,
  MetricsDecisionsResponse,
  MetricsErrorsResponse,
  MetricsLatencyResponse,
  MetricsNodeStatusCountsResponse,
  MetricsSummaryResponse,
  ParsedModelDataBE,
  ReviewCase,
  ReviewCaseStatusPending,
  ReviewCasesAvailableFiltersResponse,
  ReviewCaseWithDecisionData,
  ReviewSummaryResponse,
  UploadModelSignedUrlData,
} from "src/api/types";
import { buildCompressRequestTransformer } from "src/api/utils";
import {
  AgentExecutionApi,
  AgentManagementApi,
  CopilotExecutionApi,
  CredentialsApi,
} from "src/clients/agent-management";
import { DefaultApi as AnalyticsDashboardApi } from "src/clients/analytics-dashboard";
import { DefaultApi as AnalyticsQueryApi } from "src/clients/analytics-query";
import {
  CaseHintsApi,
  CasesApi,
  CaseTemplatesApi,
} from "src/clients/case-management";
import { CaseQueuesApi as QueuesApi } from "src/clients/case-management-service";
import { DefaultApi as CopilotApi } from "src/clients/copilot";
import { DefaultApi as EntitiesApi } from "src/clients/entities";
import {
  EventsApi,
  FeatureQueriesApi,
  FeaturesApi,
} from "src/clients/features-control";
import {
  EventsApi as RetrieveEventsApi,
  FeaturesApi as RetrieveFeaturesApi,
} from "src/clients/features-retrieve";
import { FilesDataApi } from "src/clients/files";
import {
  AwsAccountsApi,
  ChartsApi,
  CommentsApi,
  Configuration,
  FlowsApi,
  FlowVersionsApi,
  FoldersApi,
  ManifestBuilderApi,
  OutcomeTypesApi,
  ReviewsApi,
  TableSerializationApi,
  TrafficPoliciesApi,
  TypingApi,
  WebhooksApi,
  WorkspacesApi,
} from "src/clients/flow-api";
import {
  DecisionOriginType,
  DecisionsApi,
  NodeExecutionsApi,
} from "src/clients/history_v3";
import { RunsApi } from "src/clients/jobs";
import { DecisionApi } from "src/clients/router";
import { ProviderSdkApi, ProviderSdkBundlesApi } from "src/clients/sdk";
import { SimulationsApi } from "src/clients/simulations";
import {
  DatasetPatch,
  DatasetRowPatch,
  PutColumnPayload,
} from "src/datasets/api/queries";
import { HistoryDataResponse } from "src/datasets/api/types";
import { JSONValue } from "src/datasets/DatasetTable/types";
import {
  CreateDatasetJobPayload,
  CreateDatasetJobReturnType,
  CreateHistoryExportDatasetJob,
  DatasetJob,
  HistoryExportDatasetJob,
  JobRun,
} from "src/jobs/types";
import { type LatencyFunction } from "src/performance/types";

export const BACKEND_URL = flowApiUrl();

const configuration = new Configuration({
  // We want to set the apiKey here but cannot do so for now since we would then also set both authorization headers in each call
  basePath: BACKEND_URL,
});

export const awsAccountsApi = new AwsAccountsApi(configuration);
export const flowsApi = new FlowsApi(configuration);
export const workspacesApi = new WorkspacesApi(configuration);
export const versionsApi = new FlowVersionsApi(configuration);
export const trafficPoliciesApi = new TrafficPoliciesApi(configuration);
export const commentsApi = new CommentsApi(configuration);
export const outgoingWebhooksApi = new WebhooksApi(configuration);
export const typingsApi = new TypingApi(configuration);
export const chartsApi = new ChartsApi(configuration);
export const foldersApi = new FoldersApi(configuration);
export const flowVersionReviewsApi = new ReviewsApi(configuration);
export const manifestBuilderApi = new ManifestBuilderApi(configuration);

export const historyApi = (wsBaseUrl: string) => {
  return new DecisionsApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};
export const nodeExecutionsApi = (wsBaseUrl: string) => {
  return new NodeExecutionsApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};
export const copilotApi = (wsBaseUrl: string) => {
  return new CopilotApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};
export const entitiesApi = (baseUrl: string) => {
  return new EntitiesApi(new Configuration({ basePath: "https://" + baseUrl }));
};
export const providerSdkApi = (wsBaseUrl: string) => {
  return new ProviderSdkApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};
export const providerSdkBundlesApi = (wsBaseUrl: string) => {
  return new ProviderSdkBundlesApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};
export const outcomeTypesApi = new OutcomeTypesApi(configuration);

const FEATURES_API_PATH = "/features/control/api/v1";
export const featuresApi = (wsBaseUrl: string) => {
  return new FeaturesApi(
    new Configuration({ basePath: "https://" + wsBaseUrl + FEATURES_API_PATH }),
  );
};
export const featureQueriesApi = (wsBaseUrl: string) => {
  return new FeatureQueriesApi(
    new Configuration({ basePath: "https://" + wsBaseUrl + FEATURES_API_PATH }),
  );
};
export const eventsApi = (wsBaseUrl: string) => {
  return new EventsApi(
    new Configuration({ basePath: "https://" + wsBaseUrl + FEATURES_API_PATH }),
  );
};
const RETRIEVE_API_PATH = "/features/retrieve/api/v1";
export const retrieveEventsApi = (wsBaseUrl: string) => {
  return new RetrieveEventsApi(
    new Configuration({ basePath: "https://" + wsBaseUrl + RETRIEVE_API_PATH }),
  );
};
export const tableSerializationApi = new TableSerializationApi(configuration);

export const retrieveFeaturesApi = (wsBaseUrl: string) => {
  return new RetrieveFeaturesApi(
    new Configuration({ basePath: "https://" + wsBaseUrl + RETRIEVE_API_PATH }),
  );
};

export const filesApi = (wsBaseUrl: string) => {
  return new FilesDataApi(
    new Configuration({
      basePath: "https://" + wsBaseUrl + "/public/files/api/v1",
    }),
  );
};

const CASE_DATA_API_PATH = "/case_data";
export const caseTemplatesApi = (baseUrl: string) => {
  return new CaseTemplatesApi(
    new Configuration({ basePath: `https://${baseUrl}${CASE_DATA_API_PATH}` }),
  );
};

export const casesApi = (baseUrl: string) => {
  return new CasesApi(
    new Configuration({ basePath: `https://${baseUrl}${CASE_DATA_API_PATH}` }),
  );
};

export const caseHintsApi = (baseUrl: string) => {
  return new CaseHintsApi(
    new Configuration({ basePath: `https://${baseUrl}${CASE_DATA_API_PATH}` }),
  );
};

export const decisionApi = (baseUrl: string) => {
  return new DecisionApi(new Configuration({ basePath: `https://${baseUrl}` }));
};

const CASE_QUEUES_API_PATH = "/case_management_service";
export const caseQueuesApi = (baseUrl: string) => {
  return new QueuesApi(
    new Configuration({
      basePath: `https://${baseUrl}${CASE_QUEUES_API_PATH}`,
    }),
  );
};

export const simulationsApi = (wsBaseUrl: string) => {
  return new SimulationsApi(
    new Configuration({ basePath: "https://" + wsBaseUrl }),
  );
};

export const jobRunsApiGenerated = (baseUrl: string) => {
  return new RunsApi(new Configuration({ basePath: "https://" + baseUrl }));
};

export const agentManagementApi = (baseUrl: string) => {
  return new AgentManagementApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export const analyticsDashboardApi = (baseUrl: string) => {
  return new AnalyticsDashboardApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export const analyticsQueryApi = (baseUrl: string) => {
  return new AnalyticsQueryApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export const credentialsApi = (baseUrl: string) => {
  return new CredentialsApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export const agentExecutionApi = (baseUrl: string) => {
  return new AgentExecutionApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export const copilotExecutionApi = (baseUrl: string) => {
  return new CopilotExecutionApi(
    new Configuration({
      basePath: `https://${baseUrl}`,
    }),
  );
};

export enum ExecutionMode {
  SYNC = "sync",
  ASYNC = "async",
}

export class FlowRouterEndpoint {
  static readonly sandboxDecide = async <
    ResponseData extends GenericObject,
  >(params: {
    baseUrl: string;
    flowSlug: string;
    versionName: string;
    executionMode: ExecutionMode;
    entityId?: string;
    data: GenericObject;
  }) => {
    const payload = {
      data: params.data,
      metadata: {
        version: params.versionName,
        entity_id: params.entityId,
      },
      control: {
        execution_mode: params.executionMode,
      },
    };
    return (
      await flowRouterApiPost<DecideDecisionSuccess<ResponseData>>(
        params.baseUrl,
        `/flows/${params.flowSlug}/sandbox/decide`,
        payload,
      )
    ).data;
  };
}

export class FlowRouterMlNodeEndpoint {
  static readonly getSignedUrl = (baseUrl: string, flowSlug: string) => {
    return flowRouterApiPut<UploadModelSignedUrlData>(
      baseUrl,
      `/flows/${flowSlug}/ml-node/models`,
      {},
    );
  };

  static readonly getParsedModelData = (
    baseUrl: string,
    flowSlug: string,
    modelId: string,
  ) =>
    flowRouterApiGet<ParsedModelDataBE>(
      baseUrl,
      `/flows/${flowSlug}/ml-node/models/${modelId}`,
      {},
    );
}

type MetricsCommonParams = {
  startDate: string;
  endDate: string;
  versions?: string[];
  flowId: string;
};

type GetDecisionsParams = MetricsCommonParams & {
  interval: number;
};

type GetErrorsParams = GetDecisionsParams & {
  errorCodes: string[];
};

type GetLatencyParams = MetricsCommonParams & {
  interval: number;
  func: LatencyFunction;
};

export type GroupFilter = {
  [flowId: string]: {
    statusCodes?: string;
    flowVersionIds?: string;
    originTypes?: DecisionOriginType;
    parentFlowIds?: string;
    parentFlowVersionIds?: string;
    jobIds?: string;
    jobRunIds?: string;
  };
};

export type GetDecisionSummaryParams = {
  groupby: "flow.id";
  aggregate: "count";
  decision_id?: string;
  parent_decision_id?: string;
  entity_id?: string;
  grouping_id?: string;
  related_entity_id?: string;
  related_entity_schema?: string;
  environments?: DecisionEnvironment;
  groupfilter?: GroupFilter;
  start_start_time?: string;
  end_start_time?: string;
  case_assignees?: string;
  case_status?: ReviewCaseStatusPending | ReviewCaseStatusPending[];
};

type GetJobsSummaryParams = {
  startDate: string;
  endDate: string;
  flowId: string;
};

export type JobsSummaryBE = {
  decisions: number;
  errors: number;
};

export type JobsSummaryResponse = {
  current_period: JobsSummaryBE;
  previous_period: JobsSummaryBE;
};

export class ExporterMetricsEndpoint {
  static readonly postAvailableVersionsSearch = async (
    baseURL: string,
    { startDate, endDate, versions, flowId }: MetricsCommonParams,
  ) => {
    const response = await metricsApi.post<string[]>(
      `/operational/active_flow_versions/search`,
      {
        flow_id: flowId,
        ...(versions && versions.length > 0 && { versions }),
        start_date: startDate,
        end_date: endDate,
      },
      { baseURL },
    );

    return response.data;
  };

  static readonly getSummary = async (
    baseURL: string,
    { startDate, endDate, versions, flowId }: MetricsCommonParams,
    iceberg: boolean,
  ) => {
    const response = await metricsApi.get<MetricsSummaryResponse>(
      `/operational/summary`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          ...(versions &&
            versions.length > 0 && { versions: versions.join(",") }),
          start_date: startDate,
          end_date: endDate,
          iceberg,
        },
      },
    );

    return response.data;
  };

  static readonly getJobsSummary = async (
    baseURL: string,
    { startDate, endDate, flowId }: GetJobsSummaryParams,
  ) => {
    const response = await metricsApi.get<JobsSummaryResponse>(
      `/operational/jobs/summary`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          start_date: startDate,
          end_date: endDate,
        },
      },
    );

    return response.data;
  };

  static readonly getDecisions = async (
    baseURL: string,
    { startDate, endDate, interval, versions, flowId }: GetDecisionsParams,
    iceberg: boolean,
  ) => {
    const response = await metricsApi.get<MetricsDecisionsResponse>(
      `/operational/decisions`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          ...(versions &&
            versions.length > 0 && { versions: versions.join(",") }),
          start_date: startDate,
          end_date: endDate,
          interval_in_seconds: interval,
          fix_bin_origin_bug: "true",
          iceberg,
        },
      },
    );

    return response.data;
  };

  static readonly getErrors = async (
    baseURL: string,
    {
      startDate,
      endDate,
      interval,
      versions,
      errorCodes,
      flowId,
    }: GetErrorsParams,
    iceberg: boolean,
  ) => {
    const response = await metricsApi.get<MetricsErrorsResponse>(
      `/operational/errors`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          ...(versions &&
            versions.length > 0 && { versions: versions.join(",") }),
          start_date: startDate,
          end_date: endDate,
          interval_in_seconds: interval,
          error_codes: errorCodes.length ? errorCodes.join(",") : undefined,
          fix_bin_origin_bug: "true",
          iceberg,
        },
      },
    );

    return response.data;
  };

  static readonly getLatency = async (
    baseURL: string,
    { startDate, endDate, versions, func, interval, flowId }: GetLatencyParams,
    iceberg: boolean,
  ) => {
    const response = await metricsApi.get<MetricsLatencyResponse>(
      `/operational/latency`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          ...(versions &&
            versions.length > 0 && { versions: versions.join(",") }),
          start_date: startDate,
          end_date: endDate,
          function: func,
          interval_in_seconds: interval,
          fix_bin_origin_bug: "true",
          iceberg,
        },
      },
    );

    return response.data;
  };

  static readonly getDecisionSummary = async (
    baseURL: string,
    queryParams: GetDecisionSummaryParams,
    iceberg: boolean,
    signal?: AbortSignal,
  ) => {
    const formatedGroupFilters:
      | {
          [flowId: string]: {
            status_codes?: string[];
            flow_version_ids?: string[];
            origin_types?: string;
            parent_flow_ids?: string[];
            parent_flow_version_ids?: string[];
            job_ids?: string[];
            job_run_ids?: string[];
          };
        }
      | undefined = queryParams.groupfilter
      ? Object.fromEntries(
          Object.entries(queryParams.groupfilter).map(([key, value]) => {
            const mappedValue = {
              status_codes: value.statusCodes ? [value.statusCodes] : undefined,
              flow_version_ids: value.flowVersionIds
                ? [value.flowVersionIds]
                : undefined,
              origin_types: value.originTypes ? value.originTypes : undefined,
              parent_flow_ids: value.parentFlowIds
                ? [value.parentFlowIds]
                : undefined,
              parent_flow_version_ids: value.parentFlowVersionIds
                ? [value.parentFlowVersionIds]
                : undefined,
              job_ids: value.jobIds ? [value.jobIds] : undefined,
              job_run_ids: value.jobRunIds ? [value.jobRunIds] : undefined,
            };
            return [key, mappedValue];
          }),
        )
      : undefined;
    const response = await metricsApi.get<DecisionSummaryResponse>(
      `/decision_summary`,
      {
        baseURL,
        signal,
        params: {
          ...queryParams,
          ...(queryParams.case_status && {
            case_status: Array.isArray(queryParams.case_status)
              ? queryParams.case_status.join(",")
              : queryParams.case_status,
          }),
          ...(formatedGroupFilters && {
            groupfilter: urlSafeEncode(
              btoa(JSON.stringify(formatedGroupFilters)),
            ),
          }),
          iceberg,
        },
      },
    );

    return response.data;
  };
  static readonly getNodeStatusCounts = async (
    baseURL: string,
    jobRunId: string,
    flowId: string,
    environment: string,
    fromStartTime: string,
    untilStartTime: string,
  ) => {
    const response = await metricsApi.get<MetricsNodeStatusCountsResponse>(
      `/operational/jobs/runs/${jobRunId}/node_status_counts`,
      {
        baseURL,
        params: {
          flow_id: flowId,
          environment,
          from_start_time: fromStartTime,
          until_start_time: untilStartTime,
        },
      },
    );
    return response.data;
  };
}

type GetDatasetParams = {
  from?: number;
  size?: number;
};

export const DATASETS_PAGE_SIZE = 50;

export class ExporterDatasetsEndpoint {
  static readonly getDatasets = async (
    baseURL: string,
    flowId: string,
  ): Promise<Dataset[]> => {
    const fetchFrom = async (skip: number): Promise<Dataset[]> => {
      const response = await datasetsApi.get<Dataset[]>(`/datasets`, {
        baseURL,
        params: { flow_id: flowId, skip, limit: DATASETS_PAGE_SIZE },
      });
      const page = response.data;
      if (page.length < DATASETS_PAGE_SIZE) {
        return page;
      }
      const rest = await fetchFrom(skip + DATASETS_PAGE_SIZE);
      return [...page, ...rest];
    };

    return fetchFrom(0);
  };

  static readonly createDataset = async (
    baseURL: string,
    body: CreateDatasetFromHistory | CreateDatasetFromScratch,
  ) => {
    const response = await datasetsApi.post<Dataset>(`/datasets`, body, {
      baseURL,
    });

    return response.data;
  };

  static readonly getDataset = async (
    baseURL: string,
    datasetId: string,
    params: GetDatasetParams = {},
  ) => {
    const response = await datasetsApi.get<DatasetResponse>(
      `/datasets/${datasetId}`,
      {
        baseURL,
        params: {
          from_: params.from ?? 0,
          size: params.size ?? 100,
        },
      },
    );

    return response.data;
  };

  static readonly getDatasetAsFile = async (
    baseURL: string,
    datasetId: string,
    format: DatasetFileFormat,
  ) => {
    const response = await datasetsApi.get<string>(
      `/datasets/${datasetId}/file`,
      {
        baseURL,
        transformResponse: (data) => data,
        params: {
          format,
          include_output: true,
        },
      },
    );

    return response.data;
  };

  static readonly putColumns = async (
    baseURL: string,
    datasetId: string,
    columns: PutColumnPayload,
  ) => {
    const response = await datasetsApi.put<Dataset>(
      `/datasets/${datasetId}/columns`,
      columns,
      {
        baseURL,
      },
    );

    return response.data;
  };

  static readonly deleteColumns = async (
    baseURL: string,
    datasetId: string,
    columns: Array<{ group: DatasetColumnGroup; name: string }>,
  ) => {
    const response = await datasetsApi.delete<Dataset>(
      `/datasets/${datasetId}/columns`,
      { baseURL, data: columns },
    );

    return response.data;
  };

  static readonly patchRow = async (
    baseURL: string,
    datasetId: string,
    rowId: string,
    patch: DatasetRowPatch,
  ) => {
    const response = await datasetsApi.patch<DatasetRow>(
      `/datasets/${datasetId}/rows/${rowId}`,
      patch,
      {
        baseURL,
        responseType: "json",
        transformRequest: buildCompressRequestTransformer(),
      },
    );

    return response.data;
  };

  static readonly deleteDataset = async (
    baseURL: string,
    datasetId: string,
  ) => {
    const response = await datasetsApi.delete(`/datasets/${datasetId}`, {
      baseURL,
    });

    return response.data;
  };

  static readonly createDatasetFileUpload = async (
    baseURL: string,
    flowId: string,
    fileName: string,
    inputColumnsToOverwrite: DatasetColumn[],
    purpose: DatasetPurpose,
  ) => {
    const response = await datasetsApi.post<DatasetFileUpload>(
      `/dataset_file_uploads`,
      {
        flow_id: flowId,
        file_name: fileName,
        input_columns_to_overwrite: inputColumnsToOverwrite,
        purpose,
      },
      { baseURL },
    );

    return response.data;
  };

  static readonly getDatasetFileUpload = async (
    baseURL: string,
    id: string,
  ) => {
    const response = await datasetsApi.get<DatasetFileUpload>(
      `/dataset_file_uploads/${id}`,
      {
        baseURL,
      },
    );

    return response.data;
  };

  static readonly postRows = async (
    baseURL: string,
    datasetId: string,
    payload:
      | { source: "blank"; new_row_ids: string[] }
      | { source: "row_id"; row_id: string; new_row_ids: string[] }
      | {
          source: "decision_id";
          decision_id: string;
          new_row_ids: string[];
        },
  ) => {
    const response = await datasetsApi.post<DatasetRow[]>(
      `/datasets/${datasetId}/rows`,
      payload,
      { baseURL },
    );

    return response.data;
  };

  static readonly deleteRow = async (
    baseURL: string,
    datasetId: string,
    rowId: string,
  ) => {
    const response = await datasetsApi.delete<DatasetRow[]>(
      `/datasets/${datasetId}/rows/${rowId}`,
      { baseURL },
    );

    return response.data;
  };

  static readonly patchDataset = async (
    baseURL: string,
    datasetId: string,
    patch: DatasetPatch,
    etag?: string,
  ) => {
    const response = await datasetsApi.patch<Dataset>(
      `/datasets/${datasetId}`,
      patch,
      { baseURL, headers: etag ? { "If-Match": etag } : undefined },
    );

    return response.data;
  };

  static readonly fillColumn = async (
    baseURL: string,
    datasetId: string,
    group: DatasetColumnGroup,
    name: string,
    fillValue: JSONValue,
  ) => {
    const response = await datasetsApi.patch<Dataset>(
      `/datasets/${datasetId}/columns/${group}/${encodeURIComponent(name)}`,
      { fill_value: fillValue },
      { baseURL },
    );

    return response.data;
  };
}

export class ExporterHistoryEndpoint {
  static readonly getDecisionHistoryPreview = ({
    baseUrl,
    flowSlug,
    flowVersionIds,
    timeWindow,
    statusCodes,
    trafficPolicyId,
    subSamplingSize,
    outcomeFilters,
    iceberg,
    environment,
  }: {
    baseUrl: string;
    flowSlug: string;
    flowVersionIds: string[];
    timeWindow: [string, string];
    statusCodes: string[];
    trafficPolicyId?: string;
    subSamplingSize?: number;
    outcomeFilters?: DecisionsOutcomeFilter[];
    iceberg: boolean;
    environment: DecisionEnvironment;
  }) => {
    const queryParams = new URLSearchParams({
      start_date: timeWindow[0],
      end_date: timeWindow[1],
      preview_limit: "100",
      flow_slug: flowSlug,
      versions: flowVersionIds.join(","),
      status_codes: statusCodes.join(","),
      assemble_async: "true",
      iceberg: iceberg.toString(),
      // The preview names live `prod`, unlike every other decision endpoint.
      environment:
        environment === DecisionEnvironment.LIVE ? "prod" : environment,
    });
    if (trafficPolicyId) {
      queryParams.append("traffic_policy_id", trafficPolicyId);
    }
    if (subSamplingSize) {
      queryParams.append("sub_sampling_size", subSamplingSize.toString());
    }
    if (outcomeFilters) {
      queryParams.append(
        "outcome_filters",
        btoa(JSON.stringify(outcomeFilters)),
      );
    }

    return decisionHistoryApiRoute.get<HistoryDataResponse>(
      `/decisions/export/preview`,
      { baseURL: baseUrl, params: queryParams },
    );
  };
}

export type ListReviewCasesParams = {
  limit?: number;
  order?: "asc" | "desc";
  order_by?: "decision_start_time" | "created_at";
  after_timestamp?: string;
  before_timestamp?: string;
  flow_version_names?: string;
  assignees?: string;
  statuses?: string;
  environment?: "prod" | "sandbox";
  entity_or_decision_id?: string;
  node_names?: string;
};

export type CasePatchBody = {
  response_data?: Nullable<GenericObject>;
  assignee?: Nullable<string>;
  status?: ReviewCaseStatusPending;
  etag?: string;
};

export class FlowRouterReviewCasesEndpoint {
  static readonly getCases = async (
    baseURL: string,
    flowSlug: string,
    params: ListReviewCasesParams = {},
  ) => {
    const response = await reviewApi.get<ListReviewCasesResponse>(
      `/flows/${flowSlug}/cases`,
      {
        baseURL,
        params: {
          ...params,
          order: params.order ?? "desc",
          limit: params.limit ?? 100,
        },
      },
    );
    return response.data;
  };

  static readonly getCase = async (
    baseURL: string,
    flowSlug: string,
    caseId: string,
  ) => {
    const response = await reviewApi.get<ReviewCaseWithDecisionData>(
      `/flows/${flowSlug}/cases/${caseId}`,
      {
        baseURL,
      },
    );
    return response.data;
  };

  static readonly patchCase = async (
    baseURL: string,
    flowSlug: string,
    caseId: string,
    data: CasePatchBody,
    etag?: string,
  ) => {
    const response = await reviewApi.patch<ReviewCase>(
      `/flows/${flowSlug}/cases/${caseId}`,
      data,
      { baseURL, headers: etag ? { "If-Match": etag } : undefined },
    );
    return response.data;
  };

  static readonly getFilters = async (
    baseURL: string,
    flowSlug: string,
    params: Pick<
      ListReviewCasesParams,
      "after_timestamp" | "before_timestamp" | "environment" | "statuses"
    > = {},
  ) => {
    const response = await reviewApi.get<ReviewCasesAvailableFiltersResponse>(
      `/flows/${flowSlug}/cases/filter_values`,
      { baseURL, params },
    );
    return response.data;
  };

  static readonly getSummary = async (
    baseURL: string,
    flowSlug: string,
    environment: "prod" | "sandbox",
  ) => {
    const response = await reviewApi.get<ReviewSummaryResponse>(
      `/flows/${flowSlug}/cases/summary`,
      { baseURL, params: { environment } },
    );
    return response.data;
  };

  static readonly submit = async (
    baseURL: string,
    flowSlug: string,
    caseId: string,
    etag: string,
  ) => {
    const response = await reviewApi.post<ReviewCase>(
      `/flows/${flowSlug}/cases/${caseId}/submit`,
      undefined,
      { baseURL, headers: { "If-Match": etag } },
    );
    return response.data;
  };
}

// Job types that should be routed to /history_tasks API
const HISTORY_TASKS_JOB_TYPES = new Set<CreateDatasetJobPayload["type"]>([
  "assemble_simulation",
  "simulation_failures",
  "simulation_download",
]);

export class ExporterDatasetJobsEndpoint {
  static readonly createDatasetJob = async <
    T extends CreateDatasetJobPayload["type"],
  >(
    baseURL: string,
    payload: Extract<CreateDatasetJobPayload, { type: T }>,
    iceberg?: boolean,
  ): Promise<CreateDatasetJobReturnType<T>> => {
    // Route simulation jobs to /history_tasks API, others to /dataset_jobs API
    const isHistoryTask = HISTORY_TASKS_JOB_TYPES.has(payload.type);
    const api = isHistoryTask ? historyTasksApi : datasetJobsApi;
    const endpoint = isHistoryTask ? "/history_tasks" : "/dataset_jobs";

    const response = await api.post<CreateDatasetJobReturnType<T>>(
      endpoint,
      { ...payload.body, type: payload.type },
      {
        baseURL,
        params: { iceberg },
      },
    );

    return response.data;
  };

  static readonly getDatasetJobs = async ({
    baseURL,
    flowId,
    limit,
    offset,
    statuses,
  }: {
    baseURL: string;
    flowId: string;
    limit: number;
    offset: number;
    statuses: DatasetJob["status"][];
  }) => {
    const response = await datasetJobsApi.get<DatasetJob[]>(`/dataset_jobs`, {
      baseURL,
      params: {
        flow_id: flowId,
        limit,
        offset,
        statuses: statuses.join(","),
      },
    });

    return response.data;
  };

  static readonly hideDatasetJob = async (baseURL: string, id: string) => {
    const response = await datasetJobsApi.patch<DatasetJob>(
      `/dataset_jobs/${id}/hide`,
      undefined,
      {
        baseURL,
      },
    );

    return response.data;
  };

  static readonly getDatasetJob = async ({
    baseURL,
    id,
  }: {
    baseURL: string;
    id: string;
  }) => {
    const response = await datasetJobsApi.get<DatasetJob>(
      `/dataset_jobs/${id}`,
      {
        baseURL,
      },
    );

    return response.data;
  };
}

export class ExportDecisionsEndpoint {
  static readonly exportDecisionsAsync = async (
    baseURL: string,
    body: CreateHistoryExportDatasetJob,
    iceberg?: boolean,
  ) => {
    const response =
      await decisionHistoryApiRoute.post<HistoryExportDatasetJob>(
        `/decisions/export`,
        { ...body, type: "history_export" },
        {
          baseURL,
          params: { iceberg },
        },
      );

    return response.data;
  };

  static readonly pollDecisionExport = async ({
    baseURL,
    id,
  }: {
    baseURL: string;
    id: string;
  }) => {
    const response = await decisionHistoryApiRoute.get<DatasetJob>(
      `/decisions/export/${id}`,
      {
        baseURL,
      },
    );

    return response.data;
  };
}

export type CreateMultiversionTestRunVersion = {
  id: string;
  etag: string;
  children?: CreateMultiversionTestRunVersion[];
};
export type CreateMultiversionTestRunParams = {
  dataset_id: string;
  infer_types: boolean;
  flow_versions: CreateMultiversionTestRunVersion[];
  flow_id: string;
  flow_slug: string;
};

export type TestRunVersionProgress = {
  total: number;
  successful: number;
  failed: number;
  ignored: number;
  output_mismatch: number;
};

export type MultiversionTestRunVersionResults =
  CreateMultiversionTestRunVersion & {
    execution_results: FlowRunnerResultV2 | null;
    progress: TestRunVersionProgress | null;
    jobRun: JobRun | null;
  };

export type MultiversionTestRunResults = {
  id: string;
  infer_types: boolean;
  created_by: string;
  created_at: string;
  flow_versions: MultiversionTestRunVersionResults[];
  dataset: {
    id: string;
    etag: string;
    shard_count: number;
  };
};

export class RouterMultiversionTestRun {
  static readonly post = (
    baseUrl: string,
    params: CreateMultiversionTestRunParams,
  ) =>
    flowRouterApiPost<MultiversionTestRunResults>(
      baseUrl,
      `/test-runs`,
      params,
    );
  static readonly get = (baseUrl: string, runId: string) =>
    flowRouterApiGet<MultiversionTestRunResults>(
      baseUrl,
      `/test-runs/${runId}`,
      {},
    );
}

export type SimulationAnalyticsPresignedUrlRequest = {
  type: "simulation_analytics";
  job_run_id: string;
};

export type AnalyticsPresignedUrlRequest = {
  type: "analytics";
};

export type VersionNodeResultsPresignedUrlRequest = {
  type: "node_results";
  run_id: string;
  version_id: string;
  node_id: string;
  format: "json" | "csv";
};
export type VersionNodeResultsPagePresignedUrlRequest = {
  type: "node_results_page";
  run_id: string;
  version_id: string;
  node_id: string;
  page: number;
  status:
    | "success"
    | "failure"
    | "ignored"
    | "success_mismatch"
    | "success_match"
    | "errors";
};
export type NodeAnalyticsPresignedUrlRequest = {
  type: "node_analytics";
  run_id: string;
  version_id: string;
  node_id: string;
};

export type OutcomePresignedUrlRequest = {
  type: "outcome_data";
  dataset_id: string;
  dataset_etag: string;
};

export type NodeResultSummaryPresignedUrlRequest = {
  type: "node_result_summary";
  run_id: string;
  version_id: string;
  node_id: string;
};

export type PresignedUrlRequest =
  | SimulationAnalyticsPresignedUrlRequest
  | AnalyticsPresignedUrlRequest
  | VersionNodeResultsPresignedUrlRequest
  | VersionNodeResultsPagePresignedUrlRequest
  | NodeAnalyticsPresignedUrlRequest
  | OutcomePresignedUrlRequest
  | NodeResultSummaryPresignedUrlRequest;

export class RouterPresignedUrls {
  static readonly post = (
    baseUrl: string,
    testRunId: string,
    params: PresignedUrlRequest,
  ) =>
    flowRouterApiPost<{ url: string }>(
      baseUrl,
      `/test-runs/${testRunId}/presigned_urls`,
      params,
    );
}
